import React, { SyntheticEvent, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Menu from '@material-ui/core/Menu';
import Tooltip from '@material-ui/core/Tooltip';
import { makeStyles } from '@material-ui/core/styles';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import TouchAppIcon from '@material-ui/icons/TouchApp';
import clsx from 'clsx';

import { useHasAccessTo } from 'Authorized';
import Authorized from 'Authorized/Authorized';
import PERMISSIONS from 'Authorized/PERMISSIONS';

import { ILinkConfig } from 'types';

//import { ILinkConfig } from 'types';
import AdminConfigIcon from './Icons/AdminConfigIcon';
import ShopBusinessReviewIcon from './Icons/ShopBusinessReviewIcon';
import ShopReviewIcon from './Icons/ShopReviewIcon';
import SwipeIcon from './Icons/SwipeIcon';

//import { Links } from './LINKS_LIST';

const useStyles = makeStyles({
	navSideBar: {
		height: '88vh',
		overflowX: 'hidden',
		'&::-webkit-scrollbar': {
			display: 'none',
		},
	},
	rightIcon: {
		display: 'flex',
	},
	wrapText: {
		whiteSpace: 'pre-wrap',
	},
	icon: {
		color: 'white',
	},
	divider: {
		background: '#EEECF91A',
	},
	menu: {
		'& .MuiMenu-paper': {
			background: '#1D1D1B',
		},
	},
	activeTab: {
		borderLeft: '5px solid #00afcb',
	},
	activeTextColor: {
		color: '#00afcb',
	},
	inactiveText: {
		color: '#fff',
	},
});

const isActiveTab = (path: string, href?: string) => path === href;

const SideBarLinkItem: React.FC<{ data: ILinkConfig; isSub?: boolean }> = ({
	data: { href, text, Icon, sublist, permission, isRedirect },
	isSub = false,
}) => {
	const classes = useStyles();
	const location = useLocation();
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

	const handleRequestOpen = (event: SyntheticEvent<HTMLElement>) => {
		event.stopPropagation();
		setAnchorEl(event.currentTarget);
	};
	const handleRequestClose = () => setAnchorEl(null);

	if (Array.isArray(sublist)) {
		const isActive = () =>
			isActiveTab(location.pathname, href) || sublist.some((item) => isActiveTab(location.pathname, item.href));

		const idMenu = `hoverable-menu-${text}`;
		return (
			<Authorized to={permission}>
				<>
					<Tooltip title={text} placement="right">
						<ListItem
							button
							className={isActive() ? classes.activeTab : ''}
							onClick={handleRequestOpen}
							aria-owns={anchorEl !== null ? idMenu : undefined}
							aria-haspopup="true">
							<ListItemIcon classes={{ root: classes.icon }}>{Icon && <Icon />}</ListItemIcon>
							<Grid alignItems="center" justify="space-between" wrap="nowrap" container>
								<ListItemText
									primary={text}
									className={clsx(
										classes.wrapText,
										isActive() ? classes.activeTextColor : classes.inactiveText,
									)}
								/>
								<KeyboardArrowRightIcon
									className={classes.rightIcon}
									htmlColor={isActive() ? '#00afcb' : '#fff'}
								/>
							</Grid>
						</ListItem>
					</Tooltip>
					<Menu
						id={idMenu}
						anchorEl={anchorEl}
						open={Boolean(anchorEl)}
						onClose={handleRequestClose}
						keepMounted
						className={classes.menu}
						onClick={handleRequestClose}>
						{sublist.map((item) => (
							<SideBarLinkItem key={item.text} data={item} isSub={true} />
						))}
					</Menu>
					<Divider className={classes.divider} />
				</>
			</Authorized>
		);
	}

	const Component = isRedirect ? 'a' : NavLink;

	return (
		<Authorized to={permission}>
			<Tooltip
				title={text}
				disableFocusListener={isSub}
				disableHoverListener={isSub}
				disableTouchListener={isSub}
				placement="right">
				<ListItem
					component={Component}
					href={href}
					to={href}
					style={{ color: 'currentcolor' }}
					button
					className={isActiveTab(location.pathname, href) ? classes.activeTab : ''}>
					{Icon && (
						<ListItemIcon classes={{ root: classes.icon }}>
							<Icon />
						</ListItemIcon>
					)}
					<ListItemText
						primary={text}
						className={clsx(
							classes.wrapText,
							isActiveTab(location.pathname, href) ? classes.activeTextColor : classes.inactiveText,
						)}
					/>
				</ListItem>
			</Tooltip>
			<Divider className={classes.divider} />
		</Authorized>
	);
};

const SideBarLinks: React.FC = () => {
	const classes = useStyles();
	const hasAccessTo = useHasAccessTo();

	const getLinks = (): Array<ILinkConfig> => {
		const links: Array<ILinkConfig> = [];

		// Shop Picture Reject link
		if (hasAccessTo(PERMISSIONS.CAN_VIEW_SHOP_REJECT)) {
			links.push({
				text: 'Shop Picture Reject',
				Icon: SwipeIcon,
				permission: [PERMISSIONS.CAN_VIEW_SHOP_REJECT],
				href: '/merchantkyc/shop-reject',
			});
		}

		// Manual KYC links (only if user doesn't have Shop Reject permission)
		if (!hasAccessTo(PERMISSIONS.CAN_VIEW_SHOP_REJECT)) {
			links.push({
				text: 'Manual KYC',
				Icon: TouchAppIcon,
				permission: [
					PERMISSIONS.CAN_VIEW_MANUAL_KYC_ADMIN,
					PERMISSIONS.CAN_VIEW_MANUAL_KYC,
					PERMISSIONS.CAN_VIEW_MANUAL_KYC_QA,
					PERMISSIONS.CAN_VIEW_SHOP_VALIDATOR,
					PERMISSIONS.CAN_VIEW_SHOP_VALIDATOR_ADMIN,
				],
				sublist: [
					{
						text: 'KYC Admin',
						href: '/merchantkyc/manual-kyc/admin',
						permission: [PERMISSIONS.CAN_VIEW_MANUAL_KYC_ADMIN, PERMISSIONS.CAN_VIEW_SHOP_VALIDATOR_ADMIN],
					},
					{
						text: 'KYC View',
						href: '/merchantkyc/manual-kyc',
						permission: [
							PERMISSIONS.CAN_VIEW_MANUAL_KYC_ADMIN,
							PERMISSIONS.CAN_VIEW_MANUAL_KYC,
							PERMISSIONS.CAN_VIEW_MANUAL_KYC_QA,
							PERMISSIONS.CAN_VIEW_SHOP_VALIDATOR,
							PERMISSIONS.CAN_VIEW_SHOP_VALIDATOR_ADMIN,
						],
					},
				],
			});
		}

		// Shop Review link for KYC Operations permission
		if (hasAccessTo(PERMISSIONS.CAN_POS_IMAGE_MAKER)) {
			links.push({
				text: 'Shop Review',
				Icon: ShopReviewIcon,
				permission: [PERMISSIONS.CAN_POS_IMAGE_MAKER],
				href: '/merchantkyc/shop-review',
			});
		}

		// Shop Business Review link for KYC Operations permission
		if (hasAccessTo(PERMISSIONS.CAN_POS_IMAGE_CHECKER)) {
			links.push({
				text: 'Shop Business Review',
				Icon: ShopBusinessReviewIcon,
				permission: [PERMISSIONS.CAN_POS_IMAGE_CHECKER],
				href: '/merchantkyc/shop-business-review',
			});
		}

		// Admin Configuration link
		if (hasAccessTo(PERMISSIONS.CAN_POS_IMAGE_ADMIN)) {
			links.push({
				text: 'Admin Config',
				Icon: AdminConfigIcon,
				permission: [PERMISSIONS.CAN_POS_IMAGE_ADMIN],
				href: '/merchantkyc/admin-config',
			});
		}

		return links;
	};

	const Links = getLinks();

	return (
		<List component="nav" className={classes.navSideBar}>
			{Links.map((item) => (
				<SideBarLinkItem key={item.text} data={item} />
			))}
		</List>
	);
};

export default React.memo(SideBarLinks);
